import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { jobsAPI, documentsAPI, ocrAPI, tenantsAPI } from '../lib/api';
import RequiredDocumentsChecklist from '../components/RequiredDocumentsChecklist';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  FileText,
  Upload,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Trash2,
  RefreshCw,
  FileImage,
  FileIcon,
  Download,
  X,
  CloudUpload,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';

const DocumentsPage = () => {
  const { user, hasModule } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [checklistRefreshKey, setChecklistRefreshKey] = useState(0);
  const [filters, setFilters] = useState({
    module_id: '',
    status: '',
    document_type: '',
  });
  const [uploadForm, setUploadForm] = useState({
    module_id: 'timesheet_ocr',
    document_type: 'timesheet',
    files: [],
  });
  const [submissionTypes, setSubmissionTypes] = useState([]);

  useEffect(() => {
    if (!hasModule('document_submission')) return;
    tenantsAPI
      .getMyDocumentTypes()
      .then((res) => setSubmissionTypes(res.data || []))
      .catch(() => setSubmissionTypes([]));
  }, [hasModule]);

  useEffect(() => {
    loadDocuments();
  }, [filters]);

  const loadDocuments = async () => {
    try {
      const params = {
        tenant_id: user?.tenant_id || 'default',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
      };
      const response = await documentsAPI.list(params);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadForm(prev => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.dataTransfer.files)],
      }));
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadForm(prev => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files)],
      }));
    }
  };

  const removeFile = (index) => {
    setUploadForm(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleUpload = async () => {
    if (uploadForm.files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of uploadForm.files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tenant_id', user?.tenant_id || 'default');
        formData.append('module_id', uploadForm.module_id);
        formData.append('document_type', uploadForm.document_type);

        await documentsAPI.upload(formData);
        successCount++;
      } catch (error) {
        console.error('Upload failed:', error);
        failCount++;
      }
    }

    setUploading(false);
    
    if (successCount > 0) {
      toast.success(`${successCount} document(s) uploaded successfully`);
      setUploadDialogOpen(false);
      setUploadForm(prev => ({ ...prev, files: [] }));
      loadDocuments();
      setChecklistRefreshKey((k) => k + 1);
    }
    
    if (failCount > 0) {
      toast.error(`${failCount} document(s) failed to upload`);
    }
  };

  const handleProcessOCR = async (documentId) => {
    try {
      await jobsAPI.queueOCR(documentId);
      toast.success('Document queued for OCR processing');
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue OCR');
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await documentsAPI.delete(documentId);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'status-pending' },
      processing: { label: 'Processing', className: 'status-processing' },
      ocr_complete: { label: 'OCR Complete', className: 'status-complete' },
      needs_review: { label: 'Needs Review', className: 'status-review' },
      reviewed: { label: 'Reviewed', className: 'bg-blue-100 text-blue-700' },
      approved: { label: 'Approved', className: 'status-approved' },
      rejected: { label: 'Rejected', className: 'status-rejected' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return FileImage;
    return FileIcon;
  };

  const moduleOptions = [
    { value: 'timesheet_ocr', label: 'Timesheet OCR' },
    { value: 'accounting_intake', label: 'Accounting Intake' },
    { value: 'document_submission', label: 'Document Submission' },
  ];

  const documentTypeOptions = {
    timesheet_ocr: [
      { value: 'timesheet', label: 'Timesheet' },
    ],
    accounting_intake: [
      { value: 'receipt', label: 'Receipt' },
      { value: 'invoice', label: 'Invoice' },
      { value: 'contract', label: 'Contract' },
    ],
    document_submission: submissionTypes.length
      ? submissionTypes.map((t) => ({ value: t.key, label: t.label }))
      : [{ value: 'general', label: 'General Document' }],
  };

  const handleChecklistUpload = (type) => {
    setUploadForm({
      module_id: 'document_submission',
      document_type: type.key,
      files: [],
    });
    setUploadDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Documents</h1>
          <p className="text-slate-500 mt-1">Manage and process your documents</p>
        </div>
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-document-btn">
              <Upload className="mr-2 h-4 w-4" />
              Upload Documents
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Upload Documents</DialogTitle>
              <DialogDescription>
                Upload documents for OCR processing
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Module Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Module</Label>
                  <Select
                    value={uploadForm.module_id}
                    onValueChange={(value) => setUploadForm(prev => ({
                      ...prev,
                      module_id: value,
                      document_type: documentTypeOptions[value]?.[0]?.value || '',
                    }))}
                  >
                    <SelectTrigger data-testid="upload-module-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {moduleOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Document Type</Label>
                  <Select
                    value={uploadForm.document_type}
                    onValueChange={(value) => setUploadForm(prev => ({ ...prev, document_type: value }))}
                  >
                    <SelectTrigger data-testid="upload-doctype-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(documentTypeOptions[uploadForm.module_id] || []).map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Drop Zone */}
              <div
                className={`upload-zone ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <CloudUpload className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">
                  Drag and drop files here, or{' '}
                  <label className="text-indigo-600 cursor-pointer hover:underline">
                    browse
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </label>
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Supports images and PDFs up to 10MB
                </p>
              </div>

              {/* Selected Files */}
              {uploadForm.files.length > 0 && (
                <div className="space-y-2">
                  <Label>Selected Files ({uploadForm.files.length})</Label>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {uploadForm.files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium truncate max-w-[300px]">{file.name}</p>
                            <p className="text-xs text-slate-400">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFile(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading || uploadForm.files.length === 0}
                  data-testid="confirm-upload-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload {uploadForm.files.length} file(s)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Required Documents Checklist (only when module is enabled) */}
      {hasModule('document_submission') && (
        <RequiredDocumentsChecklist
          user={user}
          onUploadFor={handleChecklistUpload}
          refreshKey={checklistRefreshKey}
        />
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search documents..."
                className="pl-10"
                data-testid="search-documents"
              />
            </div>
            <Select
              value={filters.module_id || "all"}
              onValueChange={(value) => setFilters(prev => ({ ...prev, module_id: value === "all" ? "" : value }))}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-module">
                <SelectValue placeholder="All Modules" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {moduleOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) => setFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="ocr_complete">OCR Complete</SelectItem>
                <SelectItem value="needs_review">Needs Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadDocuments} data-testid="refresh-docs-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No documents found</h3>
              <p className="text-slate-500 mt-1">Upload your first document to get started</p>
              <Button className="mt-4" onClick={() => setUploadDialogOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Upload Document
              </Button>
            </div>
          ) : (
            <div className="table-container">
              <Table>
                <TableHeader>
                  <TableRow className="data-table-header">
                    <TableHead className="w-[300px]">Document</TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => {
                    const FileIconComponent = getFileIcon(doc.mime_type);
                    return (
                      <TableRow key={doc.id} className="data-table-row">
                        <TableCell className="data-table-cell">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-lg">
                              <FileIconComponent className="h-5 w-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900 truncate max-w-[200px]">
                                {doc.original_filename}
                              </p>
                              <p className="text-xs text-slate-500 font-mono">
                                {doc.id.slice(0, 8)}...
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="data-table-cell">
                          <Badge variant="outline">{doc.module_id}</Badge>
                        </TableCell>
                        <TableCell className="data-table-cell capitalize">
                          {doc.document_type}
                        </TableCell>
                        <TableCell className="data-table-cell">
                          {getStatusBadge(doc.status)}
                        </TableCell>
                        <TableCell className="data-table-cell">
                          {doc.confidence_score !== null && doc.confidence_score !== undefined ? (
                            <span className={`font-mono text-sm ${
                              doc.confidence_score >= 0.85 ? 'confidence-high' :
                              doc.confidence_score >= 0.7 ? 'confidence-medium' : 'confidence-low'
                            }`}>
                              {(doc.confidence_score * 100).toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="data-table-cell text-slate-500">
                          {format(new Date(doc.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="data-table-cell">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`doc-actions-${doc.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              {doc.status === 'pending' && (
                                <DropdownMenuItem onClick={() => handleProcessOCR(doc.id)}>
                                  <RefreshCw className="mr-2 h-4 w-4" />
                                  Process OCR
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(doc.id)}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentsPage;
